import type { ApiError, ApiResponse, HealthResponse, PartnerLoginResponse } from '@upweo/types';

/**
 * The single seam between this SPA and apps/api (Directive #4).
 *
 * Note what is NOT here: any notion of an active store. A partner developer
 * authenticates against a PartnerAccount, which is platform-level — their apps
 * and themes exist above every tenant. Store-scoped data becomes reachable
 * only through an AppInstallation, and then with that app's own credentials.
 * So this client sends X-Upweo-Partner, never X-Upweo-Shop.
 */

const API_ORIGIN = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '');
const API_BASE = `${API_ORIGIN}/partner/api/v1`;

export class ApiClientError extends Error {
  readonly status: number;
  readonly error: ApiError;

  constructor(status: number, error: ApiError) {
    super(error.message);
    this.name = 'ApiClientError';
    this.status = status;
    this.error = error;
  }
}

let accessToken: string | null = null;
let activePartnerAccountId: string | null = null;

export const setAccessToken = (token: string | null): void => {
  accessToken = token;
};
export const setActivePartnerAccount = (id: string | null): void => {
  activePartnerAccountId = id;
};

export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  readonly body?: unknown;
  readonly signal?: AbortSignal;
  readonly rootPath?: boolean;
  readonly _retrying?: boolean;
}

/** Concurrent 401s share one refresh; see the admin client for why. */
let refreshInFlight: Promise<boolean> | null = null;

const refreshAccessToken = async (): Promise<boolean> => {
  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) {
        return false;
      }
      const payload = (await response.json()) as ApiResponse<PartnerLoginResponse>;
      if (!payload.ok) {
        return false;
      }
      accessToken = payload.data.accessToken;
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
};

export const apiRequest = async <TData>(
  path: string,
  options: RequestOptions = {},
): Promise<TData> => {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }
  if (activePartnerAccountId) {
    headers['X-Upweo-Partner'] = activePartnerAccountId;
  }

  const url = options.rootPath ? `${API_ORIGIN}${path}` : `${API_BASE}${path}`;

  const response = await fetch(url, {
    method: options.method ?? 'GET',
    headers,
    credentials: 'include',
    ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  if (response.status === 401 && !options._retrying && !path.startsWith('/auth/')) {
    if (await refreshAccessToken()) {
      return apiRequest<TData>(path, { ...options, _retrying: true });
    }
  }

  const payload = (await response.json()) as ApiResponse<TData>;

  if (!response.ok || !payload.ok) {
    const error: ApiError = payload.ok
      ? {
          code: 'INTERNAL_ERROR',
          message: `Request failed with status ${String(response.status)}`,
          requestId: response.headers.get('x-request-id') ?? 'unknown',
        }
      : payload.error;
    throw new ApiClientError(response.status, error);
  }

  return payload.data;
};

export const fetchHealth = (signal?: AbortSignal): Promise<HealthResponse> =>
  apiRequest<HealthResponse>('/health', { rootPath: true, ...(signal ? { signal } : {}) });

export const restoreSession = async (): Promise<boolean> => refreshAccessToken();
